import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import NexusHeader from '../components/NexusHeader';
import { ArrowLeft, RefreshCw, AlertTriangle, Wallet, X, TrendingDown } from 'lucide-react';
import { secureStorage } from '../utils/secureStorage';
import { BACKEND_URL } from '../utils/backendUrl';

const FEATURE_LABELS = {
  soa_evaluate: 'SOA Drill - AI Coach',
  soa_ai_rescue: 'SOA Drill - AI Rescue',
  script_gen: 'Script Generation',
  storyboard_gen: 'Storyboard Generation',
  ai_assist_suggest: 'AI Assist - Suggest',
  ai_assist_validate: 'AI Assist - Validate',
  ai_assist_translate: 'AI Assist - Translate',
  ai_assist_rewrite: 'AI Assist - Rewrite',
  coaching_gen_question: 'Coaching - Question',
  coaching_evaluate: 'Coaching - Evaluate',
  coaching_summary: 'Coaching - Summary',
  ats_normalize: 'ATS Normalize',
  duration_tips: 'Duration Tips'
};

const AIUsagePage = () => {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [userBreakdown, setUserBreakdown] = useState([]);
  const [teamBreakdown, setTeamBreakdown] = useState([]);
  const [logs, setLogs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [viewMode, setViewMode] = useState('user'); // 'user' | 'team'
  const [expandedTeam, setExpandedTeam] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reconciliation, setReconciliation] = useState(null);
  const [contentMetrics, setContentMetrics] = useState(null);
  const [showResyncModal, setShowResyncModal] = useState(false);
  const [resyncForm, setResyncForm] = useState({ actual_balance: '', alert_threshold: '1', auto_calibrate: true });
  const [resyncSaving, setResyncSaving] = useState(false);
  const [resyncMsg, setResyncMsg] = useState(null);
  // GLOBAL date range — affects summary cards, by-user, by-team AND logs
  const [rangeStart, setRangeStart] = useState('');
  const [rangeEnd, setRangeEnd] = useState('');
  const [rangePreset, setRangePreset] = useState('all'); // all | week | last_week | month | last_month | custom
  // Audit-section-only filters
  const [filterModel, setFilterModel] = useState('');
  const [filterSource, setFilterSource] = useState(''); // '' | 'real' | 'estimated'
  const [distinctModels, setDistinctModels] = useState([]);
  const [logsTotal, setLogsTotal] = useState(0);
  const [showAudit, setShowAudit] = useState(false);

  // Convert range to ISO with end-of-day for end_date
  const rangeToISO = () => {
    const out = {};
    if (rangeStart) {
      out.start = new Date(`${rangeStart}T00:00:00`).toISOString();
    }
    if (rangeEnd) {
      const ed = new Date(`${rangeEnd}T23:59:59.999`);
      out.end = ed.toISOString();
    }
    return out;
  };

  const applyPreset = (key) => {
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = today.getMonth();
    const dd = today.getDate();
    const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    let s = '', e = '';
    if (key === 'week') {
      // This week (Mon -> today)
      const day = today.getDay() || 7; // Sun=0 -> 7
      const monday = new Date(yyyy, mm, dd - day + 1);
      s = fmt(monday); e = fmt(today);
    } else if (key === 'last_week') {
      const day = today.getDay() || 7;
      const lastMon = new Date(yyyy, mm, dd - day + 1 - 7);
      const lastSun = new Date(yyyy, mm, dd - day + 1 - 1);
      s = fmt(lastMon); e = fmt(lastSun);
    } else if (key === 'month') {
      s = fmt(new Date(yyyy, mm, 1)); e = fmt(today);
    } else if (key === 'last_month') {
      s = fmt(new Date(yyyy, mm - 1, 1));
      e = fmt(new Date(yyyy, mm, 0)); // last day of previous month
    } else {
      s = ''; e = '';
    }
    setRangePreset(key);
    setRangeStart(s);
    setRangeEnd(e);
  };

  useEffect(() => {
    if (!isAdmin()) navigate('/dashboard');
  }, [isAdmin, navigate]);

  const headers = { 'Authorization': `Bearer ${secureStorage.getItem('nexus_access_token')}` };

  const fetchData = async () => {
    setLoading(true);
    try {
      const { start, end } = rangeToISO();
      const baseRangeQs = new URLSearchParams();
      if (start) baseRangeQs.set('start_date', start);
      if (end) baseRangeQs.set('end_date', end);

      // Logs has its own audit-section filters layered on top of the global range
      const logsParams = new URLSearchParams(baseRangeQs);
      logsParams.set('limit', '50');
      if (selectedUser) logsParams.set('user_id', selectedUser);
      if (filterModel) logsParams.set('model', filterModel);
      if (filterSource) logsParams.set('source', filterSource);

      const reportQs = baseRangeQs.toString() ? `?${baseRangeQs.toString()}` : '';
      const [summaryRes, usersRes, teamsRes, logsRes, reconRes, contentRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/admin-panel/ai-usage/report${reportQs}`, { headers }),
        fetch(`${BACKEND_URL}/api/admin-panel/ai-usage/by-user${reportQs}`, { headers }),
        fetch(`${BACKEND_URL}/api/admin-panel/ai-usage/by-team${reportQs}`, { headers }),
        fetch(`${BACKEND_URL}/api/admin-panel/ai-usage/logs?${logsParams.toString()}`, { headers }),
        fetch(`${BACKEND_URL}/api/admin-panel/ai-balance/reconciliation`, { headers }),
        fetch(`${BACKEND_URL}/api/admin-panel/content-metrics${reportQs}`, { headers }),
      ]);
      if (summaryRes.ok) setSummary(await summaryRes.json());
      if (usersRes.ok) { const d = await usersRes.json(); setUserBreakdown(d.users || []); }
      if (teamsRes.ok) { const d = await teamsRes.json(); setTeamBreakdown(d.teams || []); }
      if (logsRes.ok) {
        const d = await logsRes.json();
        setLogs(d.logs || []);
        setLogsTotal(d.total || 0);
        if (Array.isArray(d.distinct_models)) setDistinctModels(d.distinct_models);
      }
      if (reconRes.ok) setReconciliation(await reconRes.json());
      if (contentRes.ok) setContentMetrics(await contentRes.json());
    } catch (e) { console.error('Fetch error:', e); }
    setLoading(false);
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, [selectedUser, filterModel, filterSource, rangeStart, rangeEnd]);

  // CSV Export — weekly/period report download
  const exportCSV = () => {
    const lines = [];
    const periodLabel = rangeStart && rangeEnd ? `${rangeStart} to ${rangeEnd}` : (rangeStart || 'All time');
    lines.push(`EMZI NEXUS AI Usage Report,${periodLabel}`);
    lines.push('');
    lines.push('SUMMARY');
    if (summary) {
      lines.push(`Total Credits Used,${summary.credits_used || 0}`);
      lines.push(`Real Cost USD,${summary.total_cost_usd || 0}`);
      lines.push(`Total Calls,${summary.total_calls || 0}`);
      lines.push(`Total Tokens,${summary.tokens_used || 0}`);
      lines.push(`Real Coverage %,${summary.real_coverage_pct || 0}`);
    }
    lines.push('');
    lines.push('CONTENT PRODUCTION');
    if (contentMetrics) {
      lines.push(`Total Content,${contentMetrics.total || 0}`);
      lines.push(`Drafts / In-Progress,${contentMetrics.drafts_in_progress || 0}`);
      lines.push(`Pending Approval,${contentMetrics.pending_approval || 0}`);
      lines.push(`Changes Requested,${contentMetrics.changes_requested || 0}`);
      lines.push(`Approved,${contentMetrics.approved || 0}`);
      lines.push(`Rejected,${contentMetrics.rejected || 0}`);
      lines.push(`Video Completed,${contentMetrics.video_completed || 0}`);
      lines.push('');
      lines.push('CONTENT BY OBJECTIVE');
      lines.push('Objective,Count');
      if (contentMetrics.by_objective) {
        Object.entries(contentMetrics.by_objective).forEach(([o, c]) => lines.push(`${o},${c}`));
      }
      lines.push('');
      lines.push('TOP CONTENT CREATORS');
      lines.push('Email,Name,Role,Content Count');
      (contentMetrics.top_creators || []).forEach(u => lines.push(`${u.email},${u.name || ''},${u.role || ''},${u.count}`));
    }
    lines.push('');
    lines.push('USAGE BY FEATURE');
    lines.push('Feature,Calls');
    if (summary?.feature_breakdown) {
      Object.entries(summary.feature_breakdown).forEach(([f, c]) => lines.push(`${FEATURE_LABELS[f] || f},${c}`));
    }
    lines.push('');
    lines.push('USAGE BY USER');
    lines.push('Email,Name,Role,Calls,Tokens,Credits');
    userBreakdown.forEach(u => lines.push(`${u.email},${u.name || ''},${u.role || ''},${u.calls},${u.tokens},${u.credits}`));
    lines.push('');
    lines.push('USAGE BY TEAM');
    lines.push('Team,Users,Calls,Tokens,Credits');
    teamBreakdown.forEach(t => lines.push(`${t.team},${t.user_count},${t.calls},${t.tokens},${t.credits}`));

    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ai_usage_${(rangeStart || 'all')}_to_${(rangeEnd || 'now')}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const [showRebuildModal, setShowRebuildModal] = useState(false);
  const [rebuildResult, setRebuildResult] = useState(null);
  const [rebuildRunning, setRebuildRunning] = useState(false);

  const runRebuild = async () => {
    setRebuildRunning(true);
    setRebuildResult(null);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin-panel/ai-usage/rebuild-credits`, {
        method: 'POST', headers,
      });
      const d = await res.json();
      if (res.ok) {
        setRebuildResult({ type: 'success', text: `Siap! ${d.scanned_logs} log diimbas, ${d.updated_logs} dikemas kini. Total baru: ${d.new_total_credits} credits.` });
        await fetchData();
      } else {
        setRebuildResult({ type: 'error', text: d.detail || 'Rebuild gagal' });
      }
    } catch (e) { setRebuildResult({ type: 'error', text: 'Network error: ' + e.message }); }
    setRebuildRunning(false);
  };

  const submitResync = async () => {    setResyncMsg(null);
    const bal = parseFloat(resyncForm.actual_balance);
    if (isNaN(bal) || bal < 0) { setResyncMsg({ type: 'error', text: 'Sila masukkan baki yang sah' }); return; }
    setResyncSaving(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin-panel/ai-balance/reconciliation`, {
        method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          actual_balance: bal,
          alert_threshold: parseFloat(resyncForm.alert_threshold) || 1.0,
          auto_calibrate: !!resyncForm.auto_calibrate,
        }),
      });
      const d = await res.json();
      if (res.ok) {
        setResyncMsg({
          type: 'success',
          text: d.calibration_applied
            ? `Kalibrasi dikemaskini: multiplier ${d.previous_multiplier} → ${d.new_multiplier}.`
            : 'Baseline disimpan. Tiada kalibrasi diperlukan.'
        });
        await fetchData();
        setTimeout(() => { setShowResyncModal(false); setResyncMsg(null); }, 1500);
      } else {
        setResyncMsg({ type: 'error', text: d.detail || 'Gagal menyimpan' });
      }
    } catch (e) { setResyncMsg({ type: 'error', text: 'Network error' }); }
    setResyncSaving(false);
  };

  const formatNum = (n) => n >= 1000 ? `${(n / 1000).toFixed(1)}K` : String(n);
  const formatCredits = (n) => {
    const v = Number(n) || 0;
    if (v === 0) return '0';
    if (v < 0.01) return v.toFixed(4);
    if (v < 10) return v.toFixed(2);
    return v.toFixed(1);
  };

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', background: '#F5F7FA' }}>
      <NexusHeader />

      {/* Page Header */}
      <div style={{ background: '#0e67a2', padding: '12px 16px', display: 'flex', alignItems: 'center', gap: '12px' }}>
        <button data-testid="ai-usage-back-btn" onClick={() => navigate('/dashboard')} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}>
          <ArrowLeft size={22} color="white" />
        </button>
        <div>
          <h1 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: '#fff' }}>AI Usage Monitor</h1>
          <p style={{ margin: 0, fontSize: '11px', color: 'rgba(255,255,255,0.8)' }}>Pantau penggunaan credit AI by user atau team</p>
        </div>
      </div>

      {/* Scrollable Content */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '12px 16px 100px' }}>
        {/* GLOBAL DATE RANGE BAR — drives summary, by-user, by-team & logs */}
        <Card data-testid="date-range-bar" className="border-0" style={{ borderRadius: '10px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', marginBottom: '12px' }}>
          <CardContent className="p-3">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, flexWrap: 'wrap', gap: 6 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#333' }}>📅 Tempoh Laporan</div>
              <button
                data-testid="export-csv-btn"
                onClick={exportCSV}
                style={{ padding: '5px 10px', borderRadius: 6, border: '1px solid #4CAF50', background: '#fff', color: '#2E7D32', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}
              >
                ⬇ Export CSV
              </button>
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
              {[
                { key: 'all',        label: 'Semua' },
                { key: 'week',       label: 'Minggu Ini' },
                { key: 'last_week',  label: 'Minggu Lepas' },
                { key: 'month',      label: 'Bulan Ini' },
                { key: 'last_month', label: 'Bulan Lepas' },
                { key: 'custom',     label: 'Custom' },
              ].map(p => (
                <button
                  key={p.key}
                  data-testid={`preset-${p.key}`}
                  onClick={() => applyPreset(p.key)}
                  style={{
                    padding: '5px 10px', fontSize: 11, borderRadius: 14, fontWeight: 700,
                    border: rangePreset === p.key ? '1px solid #0e67a2' : '1px solid #D0D7DE',
                    background: rangePreset === p.key ? '#0e67a2' : '#fff',
                    color: rangePreset === p.key ? '#fff' : '#444',
                    cursor: 'pointer',
                  }}
                >
                  {p.label}
                </button>
              ))}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
              <div>
                <label style={{ fontSize: 10, color: '#999', fontWeight: 600 }}>Dari</label>
                <input
                  data-testid="range-start"
                  type="date"
                  value={rangeStart}
                  onChange={(e) => { setRangePreset('custom'); setRangeStart(e.target.value); }}
                  style={{ width: '100%', padding: '6px 8px', fontSize: 11, borderRadius: 6, border: '1px solid #D0D7DE' }}
                />
              </div>
              <div>
                <label style={{ fontSize: 10, color: '#999', fontWeight: 600 }}>Hingga</label>
                <input
                  data-testid="range-end"
                  type="date"
                  value={rangeEnd}
                  onChange={(e) => { setRangePreset('custom'); setRangeEnd(e.target.value); }}
                  style={{ width: '100%', padding: '6px 8px', fontSize: 11, borderRadius: 6, border: '1px solid #D0D7DE' }}
                />
              </div>
            </div>
            {(rangeStart || rangeEnd) && (
              <div style={{ marginTop: 8, padding: '6px 10px', background: '#E3F2FD', borderRadius: 6, fontSize: 10, color: '#1565C0', textAlign: 'center' }}>
                Memaparkan data dari <b>{rangeStart || '...'}</b> hingga <b>{rangeEnd || 'sekarang'}</b>
              </div>
            )}
          </CardContent>
        </Card>

        {/* DISCREPANCY WARNING — internal tracking diverged from Emergent reality */}
        {reconciliation?.discrepancy_warning && (
          <div
            data-testid="discrepancy-banner"
            style={{
              background: '#FFF3E0', border: '2px solid #F7941E', borderRadius: '10px',
              padding: '10px 12px', marginBottom: '12px', display: 'flex', gap: '10px', alignItems: 'flex-start',
            }}
          >
            <AlertTriangle size={20} color="#F7941E" style={{ flexShrink: 0, marginTop: 2 }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '12px', fontWeight: 800, color: '#E65100' }}>Tracking Discrepancy Dikesan</div>
              <div style={{ fontSize: '11px', color: '#BF360C', marginTop: 2 }}>
                Anggaran sistem (<b>{formatCredits(reconciliation.calibrated_usage_since)}</b> cr) berbeza {formatCredits(reconciliation.discrepancy_amount)} cr dari raw tracking (<b>{formatCredits(reconciliation.raw_usage_since)}</b> cr). Klik <b>Re-sync</b> untuk auto-calibrate semula.
              </div>
            </div>
          </div>
        )}

        {/* LOW-BALANCE ALERT BANNER */}
        {reconciliation?.low_balance_alert && (
          <div
            data-testid="low-balance-banner"
            style={{
              background: '#FFEBEE', border: '2px solid #E53935', borderRadius: '10px',
              padding: '10px 12px', marginBottom: '12px', display: 'flex', gap: '10px', alignItems: 'flex-start',
            }}
          >
            <AlertTriangle size={22} color="#E53935" style={{ flexShrink: 0, marginTop: 2 }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '13px', fontWeight: 800, color: '#B71C1C' }}>Baki Kredit Rendah!</div>
              <div style={{ fontSize: '11px', color: '#B71C1C', marginTop: 2 }}>
                Predicted balance: <b>{formatCredits(reconciliation.predicted_balance)}</b> credits (di bawah had {formatCredits(reconciliation.alert_threshold)}). Top up Universal Key anda segera di Emergent panel.
              </div>
            </div>
          </div>
        )}

        {/* RECONCILIATION CARD */}
        <Card className="border-0" style={{ borderRadius: '10px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', marginBottom: '12px' }}>
          <CardContent className="p-3">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <Wallet size={16} color="#0e67a2" />
              <div style={{ fontSize: '13px', fontWeight: 700, color: '#333', flex: 1 }}>Universal Key Reconciliation</div>
              <button
                data-testid="resync-balance-btn"
                onClick={() => { setResyncForm({ actual_balance: '', alert_threshold: String(reconciliation?.alert_threshold || 1), auto_calibrate: true }); setResyncMsg(null); setShowResyncModal(true); }}
                style={{ display: 'flex', gap: 4, alignItems: 'center', padding: '4px 10px', borderRadius: 6, border: '1px solid #0e67a2', background: '#fff', color: '#0e67a2', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}
              >
                <RefreshCw size={12} /> Re-sync
              </button>
              <button
                data-testid="rebuild-credits-btn"
                onClick={() => { setRebuildResult(null); setShowRebuildModal(true); }}
                style={{ display: 'flex', gap: 4, alignItems: 'center', padding: '4px 10px', borderRadius: 6, border: '1px solid #F7941E', background: '#fff', color: '#F7941E', fontSize: 11, fontWeight: 700, cursor: 'pointer', marginLeft: 6 }}
              >
                Rebuild
              </button>
            </div>

            {reconciliation?.has_baseline ? (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
                  <div style={{ padding: 8, background: '#F7FAFC', borderRadius: 8 }}>
                    <div style={{ fontSize: 10, color: '#999', fontWeight: 600 }}>BASELINE</div>
                    <div style={{ fontSize: 16, fontWeight: 800, color: '#333' }}>{formatCredits(reconciliation.baseline_balance)}</div>
                    <div style={{ fontSize: 9, color: '#bbb' }}>{reconciliation.baseline_date ? new Date(reconciliation.baseline_date).toLocaleDateString() : '-'}</div>
                  </div>
                  <div style={{ padding: 8, background: '#FFF3E0', borderRadius: 8 }}>
                    <div style={{ fontSize: 10, color: '#999', fontWeight: 600 }}>USAGE SEJAK</div>
                    <div style={{ fontSize: 16, fontWeight: 800, color: '#F7941E' }}>−{formatCredits(reconciliation.calibrated_usage_since)}</div>
                    <div style={{ fontSize: 9, color: '#bbb' }}>multiplier ×{reconciliation.calibration_multiplier}</div>
                  </div>
                </div>
                <div style={{ padding: 10, background: '#E8F5E9', borderRadius: 8, textAlign: 'center', border: '1px solid #4CAF50' }}>
                  <div style={{ fontSize: 10, color: '#1B5E20', fontWeight: 700 }}>PREDICTED CURRENT BALANCE</div>
                  <div data-testid="predicted-balance" style={{ fontSize: 22, fontWeight: 800, color: '#1B5E20' }}>{formatCredits(reconciliation.predicted_balance)} cr</div>
                  <div style={{ fontSize: 9, color: '#666', marginTop: 2 }}>
                    Bandingkan dengan paparan di <b>Emergent → Profile → Universal Key</b>. Klik Re-sync jika berbeza.
                  </div>
                </div>

                {/* Burn Rate Forecast */}
                {reconciliation.forecast && reconciliation.forecast.avg_credits_per_day !== null && (
                  <div
                    data-testid="burn-rate-forecast"
                    style={{
                      marginTop: 8, padding: 10, borderRadius: 8,
                      background: reconciliation.forecast.days_remaining !== null && reconciliation.forecast.days_remaining <= 7 ? '#FFF3E0' : '#F0F4F8',
                      border: reconciliation.forecast.days_remaining !== null && reconciliation.forecast.days_remaining <= 7 ? '1px solid #F7941E' : '1px solid #D0D7DE',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                      <TrendingDown size={14} color={reconciliation.forecast.days_remaining !== null && reconciliation.forecast.days_remaining <= 7 ? '#F7941E' : '#0e67a2'} />
                      <div style={{ fontSize: 11, fontWeight: 700, color: '#333' }}>BURN RATE FORECAST</div>
                      {reconciliation.forecast.confidence === 'low' && (
                        <span style={{ fontSize: 9, padding: '1px 6px', borderRadius: 4, background: '#FFF8E1', color: '#F57F17', fontWeight: 600 }}>low confidence</span>
                      )}
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 8 }}>
                      <div>
                        <div style={{ fontSize: 11, color: '#666' }}>Purata</div>
                        <div style={{ fontSize: 14, fontWeight: 800, color: '#333' }}>{formatCredits(reconciliation.forecast.avg_credits_per_day)} cr/hari</div>
                        <div style={{ fontSize: 9, color: '#999' }}>{reconciliation.forecast.sample_calls} panggilan (7 hari terkini)</div>
                      </div>
                      {reconciliation.forecast.days_remaining !== null ? (
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ fontSize: 11, color: '#666' }}>Habis dalam</div>
                          <div data-testid="days-remaining" style={{
                            fontSize: 18, fontWeight: 800,
                            color: reconciliation.forecast.days_remaining <= 3 ? '#E53935' :
                                   reconciliation.forecast.days_remaining <= 7 ? '#F7941E' : '#0e67a2'
                          }}>
                            {reconciliation.forecast.days_remaining < 1 ? '< 1' : Math.round(reconciliation.forecast.days_remaining)} hari
                          </div>
                          <div style={{ fontSize: 9, color: '#999' }}>
                            {reconciliation.forecast.estimated_empty_date ? `~${new Date(reconciliation.forecast.estimated_empty_date).toLocaleDateString('ms-MY', { day: 'numeric', month: 'short' })}` : '-'}
                          </div>
                        </div>
                      ) : (
                        <div style={{ textAlign: 'right', fontSize: 10, color: '#999' }}>Baki tidak diketahui</div>
                      )}
                    </div>
                  </div>
                )}
                {reconciliation.forecast && reconciliation.forecast.avg_credits_per_day === null && reconciliation.forecast.sample_calls < 3 && (
                  <div style={{ marginTop: 8, padding: '8px 10px', borderRadius: 8, background: '#F0F4F8', fontSize: 10, color: '#666', textAlign: 'center' }}>
                    Ramalan burn rate memerlukan lebih banyak data penggunaan (sekurang-kurangnya 3 panggilan dalam 7 hari terkini).
                  </div>
                )}
              </>
            ) : (
              <div style={{ padding: 14, textAlign: 'center', background: '#F7FAFC', borderRadius: 8 }}>
                <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>Belum ada baseline. Klik <b>Re-sync</b> dan masukkan baki semasa dari panel Universal Key.</div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Summary Cards */}
        {summary && (
          <>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '8px' }}>
            <Card className="border-0" style={{ borderRadius: '10px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
              <CardContent className="p-3 text-center">
                <div data-testid="summary-credits" style={{ fontSize: '20px', fontWeight: 800, color: '#0e67a2' }}>{formatCredits(summary.credits_used)}</div>
                <div style={{ fontSize: '10px', color: '#999' }}>Total Credits Used</div>
                <div style={{ fontSize: '9px', color: '#bbb', marginTop: '2px' }}>actual_cost-based</div>
              </CardContent>
            </Card>
            <Card className="border-0" style={{ borderRadius: '10px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
              <CardContent className="p-3 text-center">
                <div data-testid="summary-cost-usd" style={{ fontSize: '20px', fontWeight: 800, color: '#7c3aed' }}>${(summary.total_cost_usd || 0).toFixed(4)}</div>
                <div style={{ fontSize: '10px', color: '#999' }}>Real Cost (USD)</div>
                <div style={{ fontSize: '9px', color: '#bbb', marginTop: '2px' }}>from LiteLLM response</div>
              </CardContent>
            </Card>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px', marginBottom: '12px' }}>
            <Card className="border-0" style={{ borderRadius: '10px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
              <CardContent className="p-3 text-center">
                <div data-testid="summary-calls" style={{ fontSize: '18px', fontWeight: 800, color: '#F7941E' }}>{summary.total_calls || 0}</div>
                <div style={{ fontSize: '10px', color: '#999' }}>Total Calls</div>
              </CardContent>
            </Card>
            <Card className="border-0" style={{ borderRadius: '10px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
              <CardContent className="p-3 text-center">
                <div data-testid="summary-tokens" style={{ fontSize: '18px', fontWeight: 800, color: '#4CAF50' }}>{formatNum(summary.tokens_used || 0)}</div>
                <div style={{ fontSize: '10px', color: '#999' }}>Total Tokens</div>
              </CardContent>
            </Card>
            <Card className="border-0" style={{ borderRadius: '10px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
              <CardContent className="p-3 text-center">
                <div data-testid="summary-real-coverage" style={{ fontSize: '18px', fontWeight: 800, color: '#0e67a2' }}>{(summary.real_coverage_pct || 0).toFixed(0)}%</div>
                <div style={{ fontSize: '10px', color: '#999' }}>Real Coverage</div>
                <div style={{ fontSize: '9px', color: '#bbb', marginTop: '2px' }}>{summary.real_calls || 0} real / {summary.estimated_calls || 0} est</div>
              </CardContent>
            </Card>
          </div>
          </>
        )}

        {/* Content Production Metrics — same date range applies */}
        {contentMetrics && (
          <Card data-testid="content-metrics-card" className="border-0" style={{ borderRadius: '10px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', marginBottom: '12px' }}>
            <CardContent className="p-3">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <div style={{ fontSize: '13px', fontWeight: 700, color: '#333' }}>📊 Content Production</div>
                <span style={{ fontSize: 10, color: '#999' }}>{contentMetrics.total} total</span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6, marginBottom: 6 }}>
                <div data-testid="content-drafts" style={{ padding: 8, background: '#F0F4F8', borderRadius: 8, textAlign: 'center', borderLeft: '3px solid #78909C' }}>
                  <div style={{ fontSize: 18, fontWeight: 800, color: '#455A64' }}>{contentMetrics.drafts_in_progress}</div>
                  <div style={{ fontSize: 9, color: '#666', fontWeight: 600 }}>📝 Drafts / In-Progress</div>
                </div>
                <div data-testid="content-pending" style={{ padding: 8, background: '#FFF3E0', borderRadius: 8, textAlign: 'center', borderLeft: '3px solid #F7941E' }}>
                  <div style={{ fontSize: 18, fontWeight: 800, color: '#E65100' }}>{contentMetrics.pending_approval}</div>
                  <div style={{ fontSize: 9, color: '#666', fontWeight: 600 }}>⏳ Pending Approval</div>
                </div>
                <div data-testid="content-changes" style={{ padding: 8, background: '#FFF8E1', borderRadius: 8, textAlign: 'center', borderLeft: '3px solid #FDD835' }}>
                  <div style={{ fontSize: 18, fontWeight: 800, color: '#F57F17' }}>{contentMetrics.changes_requested}</div>
                  <div style={{ fontSize: 9, color: '#666', fontWeight: 600 }}>✏️ Changes Req.</div>
                </div>
                <div data-testid="content-approved" style={{ padding: 8, background: '#E8F5E9', borderRadius: 8, textAlign: 'center', borderLeft: '3px solid #4CAF50' }}>
                  <div style={{ fontSize: 18, fontWeight: 800, color: '#1B5E20' }}>{contentMetrics.approved}</div>
                  <div style={{ fontSize: 9, color: '#666', fontWeight: 600 }}>✅ Approved</div>
                </div>
                <div data-testid="content-rejected" style={{ padding: 8, background: '#FFEBEE', borderRadius: 8, textAlign: 'center', borderLeft: '3px solid #E53935' }}>
                  <div style={{ fontSize: 18, fontWeight: 800, color: '#B71C1C' }}>{contentMetrics.rejected}</div>
                  <div style={{ fontSize: 9, color: '#666', fontWeight: 600 }}>❌ Rejected</div>
                </div>
                <div data-testid="content-video" style={{ padding: 8, background: '#E3F2FD', borderRadius: 8, textAlign: 'center', borderLeft: '3px solid #1E88E5' }}>
                  <div style={{ fontSize: 18, fontWeight: 800, color: '#0D47A1' }}>{contentMetrics.video_completed}</div>
                  <div style={{ fontSize: 9, color: '#666', fontWeight: 600 }}>🎬 Video Done</div>
                </div>
              </div>

              {/* By Objective */}
              {contentMetrics.by_objective && Object.keys(contentMetrics.by_objective).length > 0 && (
                <div style={{ marginTop: 8, padding: 8, background: '#FAFBFC', borderRadius: 6 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: '#666', marginBottom: 4 }}>BY OBJECTIVE</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                    {Object.entries(contentMetrics.by_objective).sort((a, b) => b[1] - a[1]).map(([k, v]) => (
                      <span key={k} data-testid={`objective-${k}`} style={{ padding: '3px 8px', background: '#fff', borderRadius: 12, fontSize: 10, border: '1px solid #E0E6ED' }}>
                        <b>{k}</b>: {v}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Top Creators */}
              {contentMetrics.top_creators && contentMetrics.top_creators.length > 0 && (
                <div style={{ marginTop: 8 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: '#666', marginBottom: 4 }}>TOP CREATORS</div>
                  {contentMetrics.top_creators.slice(0, 5).map((u, idx) => (
                    <div key={u.user_id} data-testid={`top-creator-${idx}`} style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', fontSize: 11, borderBottom: idx < 4 ? '1px solid #F5F5F5' : 'none' }}>
                      <span style={{ color: '#444' }}>
                        {idx === 0 && <span style={{ marginRight: 4 }}>👑</span>}
                        {u.name || u.email}
                      </span>
                      <span style={{ fontWeight: 700, color: '#0e67a2' }}>{u.count}</span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Feature Breakdown */}
        {summary?.feature_breakdown && Object.keys(summary.feature_breakdown).length > 0 && (
          <Card className="border-0" style={{ borderRadius: '10px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', marginBottom: '12px' }}>
            <CardContent className="p-3">
              <div style={{ fontSize: '13px', fontWeight: 700, color: '#333', marginBottom: '8px' }}>Usage by Feature</div>
              {Object.entries(summary.feature_breakdown).sort((a, b) => b[1] - a[1]).map(([feature, count]) => (
                <div key={feature} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: '1px solid #F0F0F0', fontSize: '12px' }}>
                  <span style={{ color: '#555' }}>{FEATURE_LABELS[feature] || feature}</span>
                  <span style={{ fontWeight: 600, color: '#0e67a2' }}>{count} calls</span>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Breakdown Card with User/Team toggle tabs */}
        <Card className="border-0" style={{ borderRadius: '10px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', marginBottom: '12px' }}>
          <CardContent className="p-3">
            {/* Tab toggle */}
            <div style={{ display: 'flex', gap: '4px', marginBottom: '10px', padding: '3px', background: '#F0F4F8', borderRadius: '8px' }}>
              <button
                data-testid="tab-by-user"
                onClick={() => { setViewMode('user'); setExpandedTeam(null); }}
                style={{
                  flex: 1, padding: '8px', borderRadius: '6px',
                  background: viewMode === 'user' ? '#fff' : 'transparent',
                  border: 'none', cursor: 'pointer',
                  fontSize: '12px', fontWeight: 700,
                  color: viewMode === 'user' ? '#0e67a2' : '#666',
                  boxShadow: viewMode === 'user' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                }}
              >
                By User
              </button>
              <button
                data-testid="tab-by-team"
                onClick={() => { setViewMode('team'); setSelectedUser(null); }}
                style={{
                  flex: 1, padding: '8px', borderRadius: '6px',
                  background: viewMode === 'team' ? '#fff' : 'transparent',
                  border: 'none', cursor: 'pointer',
                  fontSize: '12px', fontWeight: 700,
                  color: viewMode === 'team' ? '#0e67a2' : '#666',
                  boxShadow: viewMode === 'team' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                }}
              >
                By Team
              </button>
            </div>

            {viewMode === 'user' ? (
              <>
                <div style={{ fontSize: '13px', fontWeight: 700, color: '#333', marginBottom: '8px' }}>
                  Usage by User
                  {selectedUser && (
                    <button onClick={() => setSelectedUser(null)} style={{ marginLeft: '8px', fontSize: '11px', color: '#0e67a2', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>
                      Clear filter
                    </button>
                  )}
                </div>
                {loading ? (
                  <div style={{ textAlign: 'center', padding: '20px', color: '#999', fontSize: '12px' }}>Loading...</div>
                ) : userBreakdown.length > 0 ? (
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                      <thead>
                        <tr style={{ borderBottom: '2px solid #E0E0E0' }}>
                          <th style={{ textAlign: 'left', padding: '6px 8px', color: '#666', fontWeight: 600 }}>User</th>
                          <th style={{ textAlign: 'center', padding: '6px 8px', color: '#666', fontWeight: 600 }}>Calls</th>
                          <th style={{ textAlign: 'center', padding: '6px 8px', color: '#666', fontWeight: 600 }}>Credits</th>
                          <th style={{ textAlign: 'center', padding: '6px 8px', color: '#666', fontWeight: 600 }}>Tokens</th>
                        </tr>
                      </thead>
                      <tbody>
                        {userBreakdown.map((row, idx) => (
                          <tr
                            key={row.user_id || idx}
                            data-testid={`ai-user-row-${idx}`}
                            onClick={() => setSelectedUser(row.user_id)}
                            style={{ borderBottom: '1px solid #F0F0F0', cursor: 'pointer', background: selectedUser === row.user_id ? '#E3F2FD' : 'transparent' }}
                          >
                            <td style={{ padding: '6px 8px' }}>
                              <div style={{ fontWeight: 600, color: '#333' }}>{row.name || row.email}</div>
                              <div style={{ fontSize: '10px', color: '#999' }}>{row.role}</div>
                            </td>
                            <td style={{ textAlign: 'center', padding: '6px 8px', fontWeight: 600 }}>{row.calls}</td>
                            <td style={{ textAlign: 'center', padding: '6px 8px', fontWeight: 700, color: '#0e67a2' }}>{formatCredits(row.credits)}</td>
                            <td style={{ textAlign: 'center', padding: '6px 8px', color: '#666' }}>{formatNum(row.tokens)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div style={{ textAlign: 'center', padding: '20px', color: '#999', fontSize: '12px' }}>
                    No AI usage data yet. Data will appear after users use AI features.
                  </div>
                )}
              </>
            ) : (
              <>
                <div style={{ fontSize: '13px', fontWeight: 700, color: '#333', marginBottom: '8px' }}>Usage by Team</div>
                {loading ? (
                  <div style={{ textAlign: 'center', padding: '20px', color: '#999', fontSize: '12px' }}>Loading...</div>
                ) : teamBreakdown.length > 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    {teamBreakdown.map((team, idx) => {
                      const isExpanded = expandedTeam === team.team;
                      const maxCredits = Math.max(...teamBreakdown.map(t => t.credits), 1);
                      const pct = Math.max((team.credits / maxCredits) * 100, 2);
                      return (
                        <div
                          key={team.team}
                          data-testid={`ai-team-row-${idx}`}
                          style={{ border: '1px solid #E8EDF2', borderRadius: '8px', overflow: 'hidden', background: '#fff' }}
                        >
                          <div
                            onClick={() => setExpandedTeam(isExpanded ? null : team.team)}
                            style={{ padding: '10px 12px', cursor: 'pointer' }}
                          >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                              <div style={{ fontSize: '13px', fontWeight: 700, color: '#333' }}>
                                {idx === 0 && team.credits > 0 && <span style={{ marginRight: 6 }}>👑</span>}
                                {team.team}
                              </div>
                              <div style={{ fontSize: '14px', fontWeight: 800, color: '#0e67a2' }}>{formatCredits(team.credits)} <span style={{ fontSize: 10, color: '#999', fontWeight: 500 }}>cr</span></div>
                            </div>
                            {/* Usage bar */}
                            <div style={{ height: '4px', background: '#F0F4F8', borderRadius: '2px', overflow: 'hidden', marginBottom: '6px' }}>
                              <div style={{ width: `${pct}%`, height: '100%', background: idx === 0 ? '#F7941E' : '#0e67a2', transition: 'width 0.3s' }} />
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: '#999' }}>
                              <span>{team.user_count} user{team.user_count !== 1 ? 's' : ''}</span>
                              <span>{team.calls} calls · {formatNum(team.tokens)} tokens</span>
                              <span style={{ color: '#0e67a2', fontWeight: 600 }}>{isExpanded ? '▾ Hide users' : '▸ Top users'}</span>
                            </div>
                          </div>
                          {isExpanded && team.top_users?.length > 0 && (
                            <div style={{ background: '#FAFBFC', padding: '6px 12px 10px', borderTop: '1px solid #F0F4F8' }}>
                              {team.top_users.map((u) => (
                                <div key={u.user_id} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: '11px' }}>
                                  <span style={{ color: '#555' }}>{u.name || u.email}</span>
                                  <span style={{ fontWeight: 600, color: '#0e67a2' }}>{formatCredits(u.credits)}cr</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div style={{ textAlign: 'center', padding: '20px', color: '#999', fontSize: '12px' }}>
                    No team usage data yet.
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* Audit / Debug View — full per-call detail with filters */}
        <Card className="border-0" style={{ borderRadius: '10px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
          <CardContent className="p-3">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <div style={{ fontSize: '13px', fontWeight: 700, color: '#333' }}>
                Audit / Debug View
                <span style={{ fontSize: 10, color: '#999', marginLeft: 6 }}>({logsTotal} entries match)</span>
              </div>
              <button
                data-testid="toggle-audit-btn"
                onClick={() => setShowAudit(!showAudit)}
                style={{ padding: '4px 10px', borderRadius: 6, border: '1px solid #0e67a2', background: showAudit ? '#0e67a2' : '#fff', color: showAudit ? '#fff' : '#0e67a2', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}
              >
                {showAudit ? 'Hide details' : 'Show details'}
              </button>
            </div>

            {/* Filters (audit-only — date range is global at top of page) */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginBottom: 8 }}>
              <select
                data-testid="filter-model"
                value={filterModel}
                onChange={(e) => setFilterModel(e.target.value)}
                style={{ padding: '6px 8px', fontSize: 11, borderRadius: 6, border: '1px solid #D0D7DE' }}
              >
                <option value="">All Models</option>
                {distinctModels.map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
              <select
                data-testid="filter-source"
                value={filterSource}
                onChange={(e) => setFilterSource(e.target.value)}
                style={{ padding: '6px 8px', fontSize: 11, borderRadius: 6, border: '1px solid #D0D7DE' }}
              >
                <option value="">All Sources</option>
                <option value="real">Real (provider)</option>
                <option value="estimated">Estimated</option>
              </select>
            </div>
            {(filterModel || filterSource || selectedUser) && (
              <button
                data-testid="clear-filters-btn"
                onClick={() => { setFilterModel(''); setFilterSource(''); setSelectedUser(null); }}
                style={{ marginBottom: 8, padding: '4px 8px', fontSize: 10, color: '#0e67a2', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}
              >
                Clear audit filters
              </button>
            )}

            {logs.length > 0 ? (
              <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
                {logs.map((log, idx) => (
                  <div
                    key={idx}
                    data-testid={`audit-log-${idx}`}
                    style={{
                      padding: '8px',
                      borderBottom: '1px solid #F0F4F8',
                      fontSize: '11px',
                      background: log.source === 'real' ? '#F1F8E9' : '#FFF8E1',
                      borderLeft: log.source === 'real' ? '3px solid #4CAF50' : '3px solid #F7941E',
                      marginBottom: 4,
                      borderRadius: 4,
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                      <div>
                        <span style={{ fontWeight: 700, color: '#333' }}>{FEATURE_LABELS[log.feature] || log.feature}</span>
                        <span style={{
                          marginLeft: 6, fontSize: 9, padding: '1px 6px', borderRadius: 3, fontWeight: 700,
                          background: log.source === 'real' ? '#4CAF50' : '#F7941E',
                          color: '#fff',
                        }}>{log.source === 'real' ? 'REAL' : 'EST'}</span>
                      </div>
                      <span style={{ color: '#999', fontSize: 10 }}>{new Date(log.timestamp).toLocaleString()}</span>
                    </div>
                    <div style={{ display: 'flex', gap: 8, color: '#666', fontSize: 10, flexWrap: 'wrap' }}>
                      <span>👤 {log.email || log.user_id}</span>
                      <span>•</span>
                      <span>🤖 {log.model || 'unknown'}</span>
                    </div>
                    {showAudit && (
                      <div style={{ marginTop: 6, padding: 6, background: '#fff', borderRadius: 4, fontSize: 10, color: '#444', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4 }}>
                        <div><b>Prompt tokens:</b> {log.prompt_tokens ?? 0}</div>
                        <div><b>Completion tokens:</b> {log.completion_tokens ?? 0}</div>
                        <div><b>Total tokens:</b> {log.total_tokens ?? log.tokens ?? 0}</div>
                        <div><b>Response cost:</b> ${Number(log.response_cost_usd || 0).toFixed(6)}</div>
                        <div><b>Actual cost:</b> {formatCredits(log.actual_cost ?? log.credits)} cr</div>
                        <div><b>Source:</b> {log.source || 'estimated'}</div>
                        <div><b>Balance before:</b> {log.balance_before ?? '—'}</div>
                        <div><b>Balance after:</b> {log.balance_after ?? '—'}</div>
                      </div>
                    )}
                    {!showAudit && (
                      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 4, fontSize: 10 }}>
                        <span style={{ color: '#7c3aed' }}>${Number(log.response_cost_usd || 0).toFixed(6)}</span>
                        <span style={{ color: '#0e67a2', fontWeight: 600 }}>{formatCredits(log.actual_cost ?? log.credits)}cr</span>
                        <span style={{ color: '#999' }}>{log.total_tokens ?? log.tokens ?? 0}t</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '20px', color: '#999', fontSize: '12px' }}>
                {selectedUser || filterModel || filterSource || rangeStart || rangeEnd
                  ? 'No logs match the current filters.'
                  : 'No AI logs yet.'}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Re-sync Modal */}
      {showResyncModal && (
        <div
          data-testid="resync-modal"
          onClick={(e) => { if (e.target === e.currentTarget) setShowResyncModal(false); }}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}
        >
          <div style={{ background: '#fff', borderRadius: 12, maxWidth: 400, width: '100%', padding: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
              <Wallet size={18} color="#0e67a2" />
              <div style={{ fontSize: 15, fontWeight: 800, color: '#333', flex: 1 }}>Re-sync Baki Universal Key</div>
              <button onClick={() => setShowResyncModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#666' }}>
                <X size={18} />
              </button>
            </div>
            <div style={{ fontSize: 11, color: '#666', marginBottom: 12 }}>
              Buka <b>Emergent → Profile → Universal Key</b>, catat baki semasa, dan masukkan di bawah. Sistem akan auto-calibrate rate secara otomatik.
            </div>

            <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#333', marginBottom: 4 }}>Baki semasa (dari Emergent panel)</label>
            <input
              data-testid="actual-balance-input"
              type="number" step="0.01" min="0"
              value={resyncForm.actual_balance}
              onChange={(e) => setResyncForm({ ...resyncForm, actual_balance: e.target.value })}
              placeholder="cth: 8.02"
              style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #D0D7DE', fontSize: 14, marginBottom: 10 }}
              autoFocus
            />

            <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#333', marginBottom: 4 }}>Had amaran baki rendah</label>
            <input
              data-testid="alert-threshold-input"
              type="number" step="0.1" min="0"
              value={resyncForm.alert_threshold}
              onChange={(e) => setResyncForm({ ...resyncForm, alert_threshold: e.target.value })}
              style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #D0D7DE', fontSize: 14, marginBottom: 10 }}
            />

            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#333', marginBottom: 12 }}>
              <input
                data-testid="auto-calibrate-checkbox"
                type="checkbox"
                checked={resyncForm.auto_calibrate}
                onChange={(e) => setResyncForm({ ...resyncForm, auto_calibrate: e.target.checked })}
              />
              Auto-calibrate multiplier (disyorkan)
            </label>

            {resyncMsg && (
              <div style={{
                padding: '8px 10px', borderRadius: 8, fontSize: 11, marginBottom: 10,
                background: resyncMsg.type === 'success' ? '#E8F5E9' : '#FFEBEE',
                color: resyncMsg.type === 'success' ? '#1B5E20' : '#B71C1C',
                border: `1px solid ${resyncMsg.type === 'success' ? '#4CAF50' : '#E53935'}`,
              }}>
                {resyncMsg.text}
              </div>
            )}

            <div style={{ display: 'flex', gap: 8 }}>
              <button
                data-testid="cancel-resync-btn"
                onClick={() => setShowResyncModal(false)}
                disabled={resyncSaving}
                style={{ flex: 1, padding: 10, borderRadius: 8, border: '1px solid #D0D7DE', background: '#fff', color: '#666', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}
              >
                Batal
              </button>
              <button
                data-testid="submit-resync-btn"
                onClick={submitResync}
                disabled={resyncSaving}
                style={{ flex: 1, padding: 10, borderRadius: 8, border: 'none', background: resyncSaving ? '#999' : '#0e67a2', color: '#fff', fontSize: 13, fontWeight: 700, cursor: resyncSaving ? 'not-allowed' : 'pointer' }}
              >
                {resyncSaving ? 'Menyimpan...' : 'Simpan'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Rebuild Credits Modal */}
      {showRebuildModal && (
        <div
          data-testid="rebuild-modal"
          onClick={(e) => { if (e.target === e.currentTarget && !rebuildRunning) setShowRebuildModal(false); }}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}
        >
          <div style={{ background: '#fff', borderRadius: 12, maxWidth: 420, width: '100%', padding: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
              <AlertTriangle size={18} color="#F7941E" />
              <div style={{ fontSize: 15, fontWeight: 800, color: '#333', flex: 1 }}>Rebuild Kiraan Credits</div>
              <button onClick={() => !rebuildRunning && setShowRebuildModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#666' }}>
                <X size={18} />
              </button>
            </div>
            <div style={{ fontSize: 12, color: '#444', marginBottom: 8 }}>
              Operasi ini akan <b>kira semula</b> kolum <code>credits</code> untuk SETIAP log dalam <code>ai_usage_logs</code> menggunakan formula token-weighted terkini <code>(tokens × model_rate)</code>, kemudian rebuild aggregate <code>total_credits</code> dan <code>by_user</code>.
            </div>
            <div style={{ fontSize: 11, color: '#666', marginBottom: 12, padding: 8, background: '#FFF8E1', borderRadius: 6, border: '1px solid #F7941E' }}>
              ⚠️ Gunakan ini selepas pembaharuan rate (cth: anda baru sahaja tukar flat-rate → token-weighted). Ia idempotent — jalan berkali-kali tiada masalah.
            </div>

            {rebuildResult && (
              <div style={{
                padding: '10px 12px', borderRadius: 8, fontSize: 11, marginBottom: 12,
                background: rebuildResult.type === 'success' ? '#E8F5E9' : '#FFEBEE',
                color: rebuildResult.type === 'success' ? '#1B5E20' : '#B71C1C',
                border: `1px solid ${rebuildResult.type === 'success' ? '#4CAF50' : '#E53935'}`,
              }}>
                {rebuildResult.text}
              </div>
            )}

            <div style={{ display: 'flex', gap: 8 }}>
              <button
                data-testid="cancel-rebuild-btn"
                onClick={() => setShowRebuildModal(false)}
                disabled={rebuildRunning}
                style={{ flex: 1, padding: 10, borderRadius: 8, border: '1px solid #D0D7DE', background: '#fff', color: '#666', fontSize: 13, fontWeight: 700, cursor: rebuildRunning ? 'not-allowed' : 'pointer' }}
              >
                {rebuildResult?.type === 'success' ? 'Tutup' : 'Batal'}
              </button>
              {rebuildResult?.type !== 'success' && (
                <button
                  data-testid="confirm-rebuild-btn"
                  onClick={runRebuild}
                  disabled={rebuildRunning}
                  style={{ flex: 1, padding: 10, borderRadius: 8, border: 'none', background: rebuildRunning ? '#999' : '#F7941E', color: '#fff', fontSize: 13, fontWeight: 700, cursor: rebuildRunning ? 'not-allowed' : 'pointer' }}
                >
                  {rebuildRunning ? 'Memproses...' : 'Ya, Rebuild'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AIUsagePage;
